import { getMainSizeData, ProjectElementData, ProjectElementDataWithDetail, ProjectElementDataWithDivider, ProjectElementDataWithDocument, ProjectElementDataWithFormation, ProjectElementDataWithPDF, ProjectElementDataWithText, ProjectElementDataWithTextBox, ProjectElementDataWithWork, SelectedData, TextBlockData, WorkData } from '../dto/EntityRepository';
import { UpdateDocumentReq, UpdatePEWorkReq, UpdatePdfAssetReq, UpdateProjectElementReq, UpdateProjectElementReqDetail, UpdateProjectElementReqDivider, UpdateProjectElementReqDocument, UpdateProjectElementReqFormation, UpdateProjectElementReqPDF, UpdateProjectElementReqText, UpdateProjectElementReqTextBox, UpdateProjectElementReqWork, UpdateTextBoxReq, UpdateTextBlockReq, UpdateUploadFileReq, UpdateWorkDetailReq } from '../dto/ReqDtoRepository';
import { normalizePEInfoType, ProjectElementType, TextBlockType } from '../enum/EnumRepository';
import {
  convertChangedFormationItemToUpdateReq,
  normalizeFormationDisplaySize,
  normalizeFormationType,
} from '../util/formationUtils';
import { toUpdateTextReq } from '../util/textTag';

const convertWorkToUpdatePeReq = (
  target: ProjectElementDataWithWork
): UpdateProjectElementReqWork => {

  const updateUploadFileReq: UpdateUploadFileReq = {
    uploadFileId: target.work.uploadFile.id,
    originUrl: target.work.uploadFile.originUrl,
    allowDownload: target.work.uploadFile.allowDownload,
    width: target.work.uploadFile.width,
    height: target.work.uploadFile.height,
  };

  const updateWorkReq: UpdatePEWorkReq = {
    id: target.work.id,
    workType: target.work.workType,
    updateUploadFileReq,
    title: target.work.title,
    titleEn: target.work.titleEn ?? '',
    description: target.work.description,
    descriptionEn: target.work.descriptionEn ?? '',
    dimensionsType: target.work.dimensionsType,
    size: target.work.size,
    sizeText: target.work.sizeText,
    material: target.work.material,
    materialEn: target.work.materialEn ?? '',
    prodYear: target.work.prodYear,
    videoUrl: target.work.videoUrl,
    duration: target.work.duration,
  };

  return {
    projectElementId: target.id,
    projectElementType: ProjectElementType.WORK,
    updateWorkReq,
    displayAlignment: target.displayAlignment,
    displaySize: target.displaySize,
    infoType: normalizePEInfoType(target.infoType),
  };
};

const convertDetailToUpdatePeReq = (
  target: ProjectElementDataWithDetail
): UpdateProjectElementReqDetail => {
  const updateUploadFileReq: UpdateUploadFileReq = {
    uploadFileId: target.workDetail.uploadFile.id,
    originUrl: target.workDetail.uploadFile.originUrl,
    allowDownload: target.workDetail.uploadFile.allowDownload,
    width: target.workDetail.uploadFile.width,
    height: target.workDetail.uploadFile.height,
  };

  const updateWorkDetailReq: UpdateWorkDetailReq = {
    id: target.workDetail.id,
    workId: target.workDetail.workId,
    updateUploadFileReq,
    description: target.workDetail.description,
    detailType: target.workDetail.detailType,
    videoUrl: target.workDetail.videoUrl,
  };

  return {
    projectElementId: target.id,
    projectElementType: ProjectElementType.DETAIL,
    updateWorkDetailReq,
    displayAlignment: target.displayAlignment,
    displaySize: target.displaySize,
    infoType: normalizePEInfoType(target.infoType),
  };
};

const convertDocumentPeToUpdatePeReq = (
  target: ProjectElementDataWithDocument
): UpdateProjectElementReqDocument => {

  const updateUploadFileReq: UpdateUploadFileReq = {
    uploadFileId: target.document.uploadFile.id,
    originUrl: target.document.uploadFile.originUrl,
    allowDownload: target.document.uploadFile.allowDownload,
    width: target.document.uploadFile.width,
    height: target.document.uploadFile.height,
  };

  const updateDocumentReq: UpdateDocumentReq = {
    id: target.document.id,
    updateUploadFileReq,
    description: target.document.description,
    detailType: target.document.detailType,
    videoUrl: target.document.videoUrl,
  };

  return {
    projectElementId: target.id,
    projectElementType: ProjectElementType.DOCUMENT,
    updateDocumentReq,
    displayAlignment: target.displayAlignment,
    displaySize: target.displaySize,
    infoType: normalizePEInfoType(target.infoType),
  };
};

const convertPdfPeToUpdatePeReq = (
  target: ProjectElementDataWithPDF
): UpdateProjectElementReqPDF => {
  const updateUploadFileReq: UpdateUploadFileReq = {
    uploadFileId: target.pdfAsset.uploadFile.id,
    originUrl: target.pdfAsset.uploadFile.originUrl,
    allowDownload: target.pdfAsset.uploadFile.allowDownload,
    width: target.pdfAsset.uploadFile.width,
    height: target.pdfAsset.uploadFile.height,
  };

  const updatePdfAssetReq: UpdatePdfAssetReq = {
    id: target.pdfAsset.id,
    updateUploadFileReq,
    fileSize: target.pdfAsset.fileSize,
    description: target.pdfAsset.description,
  };

  return {
    projectElementId: target.id,
    projectElementType: ProjectElementType.PDF,
    updatePdfAssetReq,
    displayAlignment: target.displayAlignment,
  };
};

const convertTextBoxToUpdatePeReq = (
  target: ProjectElementDataWithTextBox
): UpdateProjectElementReqTextBox => {

  const updateTextBoxReq: UpdateTextBoxReq = {
    id: target.textBox.id,
    content: target.textBox.content,
  };

  return {
    projectElementId: target.id,
    projectElementType: ProjectElementType.TEXTBOX,
    updateTextBoxReq,
    textAlignment: target.textAlignment,
  };
};

const createEmptyUploadFileReq = (): UpdateUploadFileReq => ({
  uploadFileId: '',
  originUrl: '',
  allowDownload: false,
  width: 0,
  height: 0,
});

export const convertTextBlockToUpdateReq = (
  block: TextBlockData,
  index: number,
): UpdateTextBlockReq => {
  const baseReq = {
    id: block.id,
    type: block.type,
    index,
  };

  switch (block.type) {
    case TextBlockType.HEADING:
    case TextBlockType.PARAGRAPH:
    case TextBlockType.QUOTE:
    case TextBlockType.SOURCE_LINE:
      return {
        ...baseReq,
        textContent: block.textContent || '',
        url: '',
        updateUploadFileReq: createEmptyUploadFileReq(),
        caption: '',
      };
    case TextBlockType.LINK:
      return {
        ...baseReq,
        textContent: block.textContent || '',
        url: (block.url || '').trim(),
        updateUploadFileReq: createEmptyUploadFileReq(),
        caption: '',
      };
    case TextBlockType.IMAGE: {
      const uploadFile = block.uploadFile;
      return {
        ...baseReq,
        textContent: '',
        url: '',
        updateUploadFileReq: {
          uploadFileId: uploadFile?.id ?? '',
          originUrl: uploadFile?.originUrl ?? '',
          allowDownload: false,
          width: uploadFile?.width ?? 0,
          height: uploadFile?.height ?? 0,
        },
        caption: block.caption ?? '',
      };
    }
    default: {
      const exhaustive: never = block;
      throw new Error(`Unknown text block type: ${(exhaustive as TextBlockData).type}`);
    }
  }
};

const convertTextPeToUpdatePeReq = (
  target: ProjectElementDataWithText
): UpdateProjectElementReqText => {
  const changedTextBlocks: UpdateTextBlockReq[] = target.selectedTextBlocks.map((block, index) =>
    convertTextBlockToUpdateReq(block, index),
  );

  const updateTextReq = toUpdateTextReq(target.text, {
    includeTagIds: false,
    textBlocks: changedTextBlocks.length > 0 ? changedTextBlocks : undefined,
  });

  return {
    projectElementId: target.id,
    projectElementType: ProjectElementType.TEXT,
    updateTextReq,
    displayAlignment: target.displayAlignment,
    displaySize: target.displaySize,
  };
};

const convertDividerToUpdatePeReq = (
  target: ProjectElementDataWithDivider
): UpdateProjectElementReqDivider => {
  return {
    projectElementId: target.id,
    projectElementType: ProjectElementType.DIVIDER,
    dividerType: target.dividerType,
  };
};

const convertFormationToUpdatePeReq = (
  target: ProjectElementDataWithFormation,
): UpdateProjectElementReqFormation => {
  const changedItems = target.formation.items
    .filter((item) => (
      item.imageChanged ||
      item.captionChanged ||
      item.focusChanged ||
      item.referenceChanged ||
      item.presentationChanged
    ))
    .map(convertChangedFormationItemToUpdateReq);

  return {
    projectElementId: target.id,
    projectElementType: ProjectElementType.FORMATION,
    displaySize: normalizeFormationDisplaySize(target.displaySize),
    infoType: normalizePEInfoType(target.infoType),
    updateFormationReq: {
      id: target.formation.id,
      formationType: normalizeFormationType(target.formation.formationType),
      items: changedItems,
    },
  };
};

export const convertWorkToSelectedData = (work: WorkData): SelectedData => ({
  workId: work.id,
  title: work.title,
  titleEn: work.titleEn,
  originUrl: work.uploadFile?.originUrl ?? '',
  material: work.material,
  materialEn: work.materialEn,
  prodYear: work.prodYear,
  duration: work.duration,
  size: getMainSizeData(work.sizes, work.mainSizeUnit),
  sizeText: work.sizeText,
  dimensionsType: work.dimensionsType,
  price: work.price,
  isPOV: work.isPOV,
  artist: work.artist,
});

// export const convertWorkToSimpleWork = (work: WorkData): WorkSimpleData => ({
//   id: work.id,
//   title: work.title,
//   originUrl: work.uploadFile?.originUrl ?? '',
//   workType: work.workType,
//   material: work.material,
//   prodYear: work.prodYear,
//   duration: work.duration,
//   size: work.size,
//   dimensionsType: work.dimensionsType,
//   hasDetail: false,
// });

export const convertPeToUpdateReq = (
  target: ProjectElementData
): UpdateProjectElementReq => {
  switch (target.projectElementType) {
    case ProjectElementType.WORK:
      return convertWorkToUpdatePeReq(target as ProjectElementDataWithWork);
    case ProjectElementType.DETAIL:
      return convertDetailToUpdatePeReq(target as ProjectElementDataWithDetail);
    case ProjectElementType.DOCUMENT:
      return convertDocumentPeToUpdatePeReq(target as ProjectElementDataWithDocument);
    case ProjectElementType.PDF:
      return convertPdfPeToUpdatePeReq(target as ProjectElementDataWithPDF);
    case ProjectElementType.TEXT:
      return convertTextPeToUpdatePeReq(target as ProjectElementDataWithText);
    case ProjectElementType.TEXTBOX:
      return convertTextBoxToUpdatePeReq(target as ProjectElementDataWithTextBox);
    case ProjectElementType.DIVIDER:
      return convertDividerToUpdatePeReq(target as ProjectElementDataWithDivider);
    case ProjectElementType.FORMATION:
      return convertFormationToUpdatePeReq(target as ProjectElementDataWithFormation);
    default:
      throw new Error(`Unknown projectElementType`);
  }
};
